// src/utils/advanceGameweek.js
import { db } from "../lib/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { paths } from "../lib/paths";
import { applyPriceChanges } from "./priceEngine";
import { logError } from "./logError";
import { logEvent } from "./EventLogger";
import { LEAGUE_RULESETS, INFRACTION_POINTS, CANON_TO_LABEL } from "./scoringRules";
import { getLeagueConfig } from "./leagueConfig";
import { isUnlimitedFT } from "./freeTransfers";

function toNumber(value, fallback = 0) {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function emptyPlayerStats(existingStats = {}) {
  return {
    totalPoints: toNumber(existingStats.totalPoints, 0),
    appearances: toNumber(existingStats.appearances, 0),
    starts: toNumber(existingStats.starts, 0),
    goals: toNumber(existingStats.goals, 0),
    assists: toNumber(existingStats.assists, 0),
    cleanSheets: toNumber(existingStats.cleanSheets, 0),
    saves: toNumber(existingStats.saves, 0),
    bonus: toNumber(existingStats.bonus, 0),
    yellowCards: toNumber(existingStats.yellowCards, 0),
  };
}

function extractPlayerPointStats(data = {}, playerData = {}, rules = {}) {
  const breakdown = data.breakdown || {};
  const pos = playerData.position;

  const safeDiv = (num, denom) => {
    if (!denom) return 0;
    return num / denom;
  };

  return {
    totalPoints: toNumber(data.total, 0),
    appearances: safeDiv(toNumber(breakdown.appearance, 0), rules.appearance),
    starts: safeDiv(toNumber(breakdown.starter, 0), rules.starter),
    goals: safeDiv(toNumber(breakdown.goals, 0), rules.goal?.[pos]),
    assists: safeDiv(toNumber(breakdown.assists, 0), rules.assist),
    cleanSheets: (() => {
      const cs = toNumber(breakdown.cleanSheet, 0);
      const csGK = toNumber(breakdown.cleanSheetGK, 0);
      if (csGK) {
        const gkCleanSheetPoints = 7;
        return safeDiv(csGK, gkCleanSheetPoints);
      }
      return safeDiv(cs, rules.cleanSheet?.[pos]);
    })(),
    saves: safeDiv(
      toNumber(breakdown[CANON_TO_LABEL.gksavebonus], 0),
      rules.gksavebonus
    ),
    bonus: toNumber(breakdown.bonus, 0),
    yellowCards: safeDiv(
      Math.abs(toNumber(breakdown[CANON_TO_LABEL.yellowcard], 0)),
      Math.abs(INFRACTION_POINTS.yellowcard)
    ),
  };
}

function addStats(acc, delta) {
  acc.totalPoints += toNumber(delta.totalPoints, 0);
  acc.appearances += toNumber(delta.appearances, 0);
  acc.starts += toNumber(delta.starts, 0);
  acc.goals += toNumber(delta.goals, 0);
  acc.assists += toNumber(delta.assists, 0);
  acc.cleanSheets += toNumber(delta.cleanSheets, 0);
  acc.saves += toNumber(delta.saves, 0);
  acc.bonus += toNumber(delta.bonus, 0);
  acc.yellowCards += toNumber(delta.yellowCards, 0);
  return acc;
}

function selectedPlayerIdsFromSelection(selection = {}) {
  const result = [];
  for (const value of Object.values(selection || {})) {
    if (Array.isArray(value)) {
      for (const item of value) {
        if (typeof item === "string") result.push(item);
        if (item && typeof item === "object") {
          if (typeof item.playerID === "string") result.push(item.playerID);
          if (typeof item.playerId === "string") result.push(item.playerId);
          if (typeof item.id === "string") result.push(item.id);
        }
      }
    }
  }
  return [...new Set(result.filter(Boolean))];
}

// Compute next deadline based on previous deadline, matchDay, and timeOfDay (league.gameweeks settings)
function computeNextDeadlineFromPrev(prevDeadline, matchDay, timeOfDay) {
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const targetDay = days.indexOf(matchDay);
  if (targetDay === -1) throw new Error("Invalid matchDay");
  // Base from previous deadline, not current date
  let base = prevDeadline instanceof Date ? new Date(prevDeadline) : new Date(prevDeadline.toDate ? prevDeadline.toDate() : prevDeadline);
  base.setSeconds(0, 0); // clear seconds/millis
  // Find the next occurrence of the target matchDay after prevDeadline
  let d = new Date(base);
  d.setHours(0, 0, 0, 0);
  // Always go forward at least 1 day
  d.setDate(d.getDate() + 1);
  // Find next matchDay
  while (d.getDay() !== targetDay) {
    d.setDate(d.getDate() + 1);
  }
  if (timeOfDay === "Daytime") {
    // Deadline is 9am on match day
    d.setHours(9, 0, 0, 0);
  } else if (timeOfDay === "Evening") {
    // Deadline is 2:00 PM on the match day
    d.setHours(14, 0, 0, 0);
  } else if (typeof timeOfDay === "string" && timeOfDay.includes(":")) {
    // Custom time string "HH:MM" on the matchDay
    const [h, m] = timeOfDay.split(":").map(Number);
    d.setHours(h, m, 0, 0);
  } else {
    // fallback: 7 days after previous deadline at midnight
    d = new Date(base);
    d.setDate(d.getDate() + 7);
    d.setHours(0, 0, 0, 0);
  }
  return d;
}

export async function advanceGameweek(leagueID, actor = {}) {
  console.log(`⚙️ Advancing gameweek for league: ${leagueID}`);
  const actorUserID = actor?.userID || "system";
  const actorUserName = actor?.userName || "";
  const actorRole = actor?.role || (actorUserID === "system" ? "system" : "admin");
  const actorSource = actor?.source || "advance_gameweek";
  try {
    const leagueRef = paths.league(leagueID);
    const leagueSnap = await getDoc(leagueRef);
    if (!leagueSnap.exists()) throw new Error("League not found");
    const league = leagueSnap.data();

    const currentGW = league.currentGW || "GW1";
    const curNum = parseInt(currentGW.replace("GW", ""), 10);
    const nextGW = `GW${curNum + 1}`;

    const leagueConfig = getLeagueConfig(league.type || league.leagueType);
    const leagueSizeKey = String(leagueConfig.minPlayers);
    const rules = LEAGUE_RULESETS[leagueSizeKey] || LEAGUE_RULESETS["7"];

    const currentSeasonID = league.currentSeasonID || "S1";

    // 1. Mark old GW not current and update phase to "closed"
    const curGWRef = paths.gameweek(leagueID, currentGW);

    // Backfill: ensure an availability doc exists for the current GW before closing it
    try {
      const curAvailRef = doc(db, "leagues", leagueID, "seasons", currentSeasonID, "gameweeks", currentGW, "availability", "responses");
      await setDoc(curAvailRef, { responses: {}, createdAt: serverTimestamp() }, { merge: true });
    } catch (err) {
      console.warn(`⚠️ Could not backfill availability doc for ${currentGW}:`, err);
    }

    try {
      await updateDoc(curGWRef, { isCurrent: false, phase: "closed" });
    } catch (err) {
      console.error(`❌ Failed to update currentGW doc at ${curGWRef.path} with { isCurrent: false, phase: "closed" }`, err);
      await logError({
        leagueID,
        userID: "system",
        action: "advanceGameweek_currentGW_update",
        payload: { currentGW },
        error: err
      });
      throw err;
    }
    // backfill id/number on the current GW if missing (idempotent)
    try {
      await updateDoc(curGWRef, { id: currentGW, number: curNum });
    } catch (err) {
      console.error(`⚠️ Failed to backfill currentGW doc at ${curGWRef.path}`, err);
    }

    // 2. Compute deadline using league-level gameweeks.matchDay/timeOfDay, based on previous GW's deadline
    let deadline;
    try {
      // Prefer league.gameweeks.matchDay/timeOfDay if present, else fallback to league.matchDay/timeOfDay for backward compatibility
      const gwSettings = league.gameweeks || {};
      const matchDay = gwSettings.matchDay || league.matchDay;

      // Prefer explicit kickoff time (HH:MM) if available
      // Priority: league.gameweeks.kickoffTime -> league.defaultKickoffTime -> league.gameweeks.timeOfDay -> league.timeOfDay
      // Backward compatible: timeOfDay may be "Daytime"/"Evening" OR a "HH:MM" string
      const timeOfDay =
        gwSettings.kickoffTime ||
        league.defaultKickoffTime ||
        gwSettings.timeOfDay ||
        league.timeOfDay;

      // Read previous GW's deadline
      let prevGWDoc = await getDoc(curGWRef);
      let prevDeadline = prevGWDoc.exists() ? prevGWDoc.data().deadline : null;
      if (!prevDeadline) {
        // fallback: use now as base if previous deadline missing
        prevDeadline = new Date();
      } else if (typeof prevDeadline.toDate === "function") {
        prevDeadline = prevDeadline.toDate();
      }
      deadline = computeNextDeadlineFromPrev(prevDeadline, matchDay, timeOfDay);
    } catch (err) {
      console.warn("⚠️ Could not compute deadline from league.gameweeks.matchDay/timeOfDay. Falling back to +7 days.", err);
      deadline = new Date();
      deadline.setDate(deadline.getDate() + 7);
    }

    // 3. Create new GW doc
    const nextGWRef = paths.gameweek(leagueID, nextGW);

    const gwSettings = league.gameweeks || {};
    const matchDay = gwSettings.matchDay || league.matchDay;

    // Prefer explicit kickoff time (HH:MM) if available
    const timeOfDay =
      gwSettings.kickoffTime ||
      league.defaultKickoffTime ||
      gwSettings.timeOfDay ||
      league.timeOfDay;

    const kickoffTime = (typeof timeOfDay === "string" && timeOfDay.includes(":")) ? timeOfDay : undefined;

    const nextGWPayload = {
      id: nextGW,
      number: curNum + 1,
      isCurrent: true,
      phase: "pick",
      createdAt: serverTimestamp(),
      // Firestore accepts JS Date and stores as Timestamp
      deadline: deadline instanceof Date ? deadline : new Date(deadline),
      matchDay,
      timeOfDay,
      ...(kickoffTime ? { kickoffTime } : {}),
    };
    try {
      await setDoc(nextGWRef, nextGWPayload, { merge: true });
      // Create availability doc for the new GW
      try {
        const nextAvailRef = doc(db, "leagues", leagueID, "seasons", currentSeasonID, "gameweeks", nextGW, "availability", "responses");
        await setDoc(nextAvailRef, { responses: {}, createdAt: serverTimestamp() }, { merge: true });
      } catch (err) {
        console.warn(`⚠️ Could not create availability doc for ${nextGW}:`, err);
      }
    } catch (err) {
      console.error(`❌ Failed to set nextGW doc at ${nextGWRef.path}`, err);
      await logError({
        leagueID,
        userID: "system",
        action: "advanceGameweek_nextGW_creation",
        payload: { nextGW },
        error: err
      });
      throw err;
    }
    // Ensure `id`, `number`, `phase`, and `isCurrent` are definitely present even if the doc pre-existed
    try {
      await updateDoc(nextGWRef, { id: nextGW, number: curNum + 1, phase: "pick", isCurrent: true });
    } catch (_) {}

    // 4. Seed each team’s GW snapshot
    const teamsSnap = await getDocs(paths.leagueTeams(leagueID));
    // Dynamically derive positions from league config
    const leaguePositions = Array.isArray(league?.positionRules)
      ? league.positionRules.map(rule => rule.position)
      : ["DEF", "MID", "FWD"];
    // Dynamically pull budget and maxFreeTransfers from league config
    const BASE_BUDGET = Number(league?.budget ?? 24);
    const MAX_FREE_TRANSFERS = Number(league?.maxFreeTransfers ?? 2);
    const selectedByCounts = new Map();
    const totalTeamsForSelection = teamsSnap.size;

    for (const tDoc of teamsSnap.docs) {
      try {
        const teamData = tDoc.data();
        const teamID = tDoc.id;

        // last GW snapshot if exists
        const lastRef = doc(paths.gwTeams(leagueID, currentGW), teamID);
        const lastSnap = await getDoc(lastRef);
        const last = lastSnap.exists() ? lastSnap.data() : {};

        // --- Fallback guard: if last.selections[currentGW] is empty or undefined, read league-level doc version
        if (
          !last.selections ||
          !last.selections[currentGW] ||
          Object.values(last.selections[currentGW]).flat().length === 0
        ) {
          const leagueTeamRef = doc(paths.leagueTeams(leagueID), teamID);
          const leagueTeamSnap = await getDoc(leagueTeamRef);
          if (leagueTeamSnap.exists()) {
            const leagueTeamData = leagueTeamSnap.data();
            if (leagueTeamData.selections && leagueTeamData.selections[currentGW]) {
              last.selections = last.selections || {};
              last.selections[currentGW] = leagueTeamData.selections[currentGW];
              last.captains = last.captains || {};
              last.captains[currentGW] = leagueTeamData.captains?.[currentGW] || "";
            }
          }
        }

        // --- Backfill squadMeta for currentGW if missing, with extra Firestore validation ---
        let shouldBackfill = false;
        if (lastSnap.exists()) {
          const squadMetaData = last.squadMeta?.[currentGW];
          const hasLocalData = squadMetaData && Object.values(squadMetaData).flat().length > 0;

          if (!hasLocalData) {
            // extra safeguard: fetch live copy if it looks empty locally
            const teamGWLiveSnap = await getDoc(
              doc(paths.gwTeams(leagueID, currentGW), teamID)
            );
            const liveData = teamGWLiveSnap.exists() ? teamGWLiveSnap.data() : {};
            const liveHasSquadMeta =
              liveData?.squadMeta?.[currentGW] &&
              Object.values(liveData.squadMeta[currentGW]).flat().length > 0;
            shouldBackfill = !liveHasSquadMeta;
          }
        }

        if (shouldBackfill) {
          // backfill squadMeta for currentGW based on selections
          const selectionsForCurrentGW = last.selections?.[currentGW] || {};
          const newSquadMeta = {};
          for (const pos of leaguePositions) {
            const playersInPos = selectionsForCurrentGW[pos] || [];
            newSquadMeta[pos] = {};
            for (const playerID of playersInPos) {
              const playerDocRef = doc(paths.leaguePlayers(leagueID), playerID);
              const playerDocSnap = await getDoc(playerDocRef);
              let currentPrice = null;
              let playerData = {};
              if (playerDocSnap.exists()) {
                playerData = playerDocSnap.data();
                if (playerData.currentPrice !== undefined) {
                  currentPrice = playerData.currentPrice;
                } else if (playerData.price !== undefined) {
                  currentPrice = playerData.price;
                }
              }
              newSquadMeta[pos][playerID] = {
                buyPrice: currentPrice,
                buyGW: playerData?.buyGW ?? currentGW,
              };
            }
          }
          const teamGWRef = doc(paths.gwTeams(leagueID, currentGW), teamID);
          await updateDoc(teamGWRef, {
            [`squadMeta.${currentGW}`]: newSquadMeta,
          });
          if (!last.squadMeta) last.squadMeta = {};
          last.squadMeta[currentGW] = newSquadMeta;

          try {
            await updateDoc(doc(paths.leagueTeams(leagueID), teamID), {
              [`squadMeta.${currentGW}`]: newSquadMeta,
              [`transfersMade.${currentGW}`]: Number(last.transfersMade?.[currentGW] ?? 0),
            });
          } catch (err) {
            console.error(`❌ Failed to patch leagueTeams/${teamID} squadMeta[${currentGW}], transfersMade during backfill`, err);
            await logError({
              leagueID,
              userID: "system",
              action: "advanceGameweek_league_team_squadMeta_backfill",
              payload: { teamID, currentGW },
              error: err
            });
            throw err;
          }
        }

        if (!lastSnap.exists()) {
          console.warn(`⚠️ No snapshot found for team ${teamID} in ${currentGW}, seeding fallback for nextGW.`);
          // If teamData.startGW === currentGW, this is a late joiner whose first GW is currentGW, so seed a proper squadMeta for currentGW.
          let emptySelections = {};
          let squadMetaForCurrent = {};
          let fallbackSquadMeta = {};
          let fallbackSelections = {};
          let fallbackBankStart = {};
          leaguePositions.forEach(pos => {
            emptySelections[pos] = [];
          });
          leaguePositions.forEach(pos => {
            squadMetaForCurrent[pos] = [];
          });
          fallbackSquadMeta[nextGW] = {};
          leaguePositions.forEach(pos => {
            fallbackSquadMeta[nextGW][pos] = [];
          });
          fallbackSelections[nextGW] = { ...emptySelections };
          // Special case: late joiner, seed correct squadMeta for currentGW
          if (teamData.startGW === currentGW) {
            // Build squadMeta[currentGW] using teamData.selections?.[currentGW]
            const sel = (teamData.selections && teamData.selections[currentGW]) ? teamData.selections[currentGW] : { ...emptySelections };
            let builtSquadMeta = {};
            for (const pos of leaguePositions) {
              builtSquadMeta[pos] = {};
              const playersInPos = sel[pos] || [];
              for (const playerID of playersInPos) {
                const playerDocRef = doc(paths.leaguePlayers(leagueID), playerID);
                const playerDocSnap = await getDoc(playerDocRef);
                let buyPrice = null;
                if (playerDocSnap.exists()) {
                  const playerData = playerDocSnap.data();
                  if (playerData.currentPrice !== undefined) buyPrice = playerData.currentPrice;
                  else if (playerData.price !== undefined) buyPrice = playerData.price;
                  else if (playerData.originalPrice !== undefined) buyPrice = playerData.originalPrice;
                }
                builtSquadMeta[pos][playerID] = {
                  buyPrice,
                  buyGW: currentGW,
                };
              }
            }
            // Seed for currentGW
            const currentGWFallbackPayload = {
              leagueID,
              teamID,
              managerID: teamData.managerID,
              selections: { [currentGW]: sel },
              squadMeta: { [currentGW]: builtSquadMeta },
              captains: { [currentGW]: "" },
              chipsUsed: { [currentGW]: "None" },
              transfersMade: { [currentGW]: Number(0) },
              transfersFree: { [currentGW]: Number(1) },
              lastSavedAt: serverTimestamp(),
            };
            // Write seed for currentGW
            const teamGWCurrentRef = doc(paths.gwTeams(leagueID, currentGW), teamID);
            await setDoc(teamGWCurrentRef, currentGWFallbackPayload); 
            // PATCH: Also update league-level team doc squadMeta for currentGW, transfersFree, and transfersMade
            try {
              // Only allow transfersFree/transfersMade for currentGW if this is a new joiner
              if (teamData.startGW === currentGW) {
                await updateDoc(doc(paths.leagueTeams(leagueID), teamID), {
                  [`squadMeta.${currentGW}`]: builtSquadMeta,
                  [`transfersFree.${currentGW}`]: Number(1),
                  [`transfersMade.${currentGW}`]: Number(0),
                });
              } else {
                // Should never happen: prevent fallback for existing teams in currentGW
                console.warn("🚫 Prevented backfill of transfersFree/transfersMade for existing currentGW", teamID, currentGW);
                await updateDoc(doc(paths.leagueTeams(leagueID), teamID), {
                  [`squadMeta.${currentGW}`]: builtSquadMeta,
                });
              }
            } catch (err) {
              console.error(`❌ Failed to patch leagueTeams/${teamID} squadMeta[${currentGW}], transfersFree, or transfersMade for late joiner`, err);
              await logError({
                leagueID,
                userID: "system",
                action: "advanceGameweek_league_team_late_joiner_patch",
                payload: { teamID, currentGW },
                error: err
              });
              throw err;
            }
            // Now build nextGW fallback as normal (empty, since no picks yet)
            leaguePositions.forEach(pos => {
              emptySelections[pos] = [];
            });
            fallbackSquadMeta[nextGW] = {};
            leaguePositions.forEach(pos => {
              fallbackSquadMeta[nextGW][pos] = {};
            });
            fallbackSelections[nextGW] = { ...emptySelections };
          }
          // Standard fallback for nextGW (empty picks, empty squadMeta)
          // Add bankStart and currentBudget for nextGW (mirroring base budget)
          fallbackBankStart = { [nextGW]: Number(BASE_BUDGET) };
          const fallbackPayload = {
            leagueID,
            teamID,
            managerID: teamData.managerID,
            selections: fallbackSelections,
            squadMeta: fallbackSquadMeta,
            captains: { [nextGW]: "" },
            chipsUsed: { [nextGW]: "None" },
            transfersMade: { [nextGW]: Number(0) },
            transfersFree: { [nextGW]: Number(1) },
            bankStart: fallbackBankStart,
            currentBudget: { [nextGW]: Number(BASE_BUDGET) },
            lastSavedAt: serverTimestamp(),
          };
          const teamGWRef = doc(paths.gwTeams(leagueID, nextGW), teamID);
          await setDoc(teamGWRef, fallbackPayload);
          // Also update league-level team doc to include currentBudget[nextGW]
          try {
            await updateDoc(doc(paths.leagueTeams(leagueID), teamID), {
              [`currentBudget.${nextGW}`]: Number(BASE_BUDGET),
            });
          } catch (err) {
            console.error(`❌ Failed to update leagueTeams/${teamID} with currentBudget[${nextGW}]`, err);
            await logError({
              leagueID,
              userID: "system",
              action: "advanceGameweek_league_team_currentBudget_update",
              payload: { teamID, nextGW },
              error: err
            });
            throw err;
          }
          continue;
        }

        // --- Transfers calculation (FPL-style rollover) ---
        const toNum = (val, def = 0) => {
          const n = Number(val);
          return Number.isFinite(n) ? n : def;
        };

        const prevFreeRaw = teamData?.transfersFree?.[currentGW];
        const prevFree = toNum(prevFreeRaw, 1);
        const prevUsed = toNum(teamData?.transfersMade?.[currentGW], 0);
        const cap = toNum(league?.maxFreeTransfers, MAX_FREE_TRANSFERS);

        // Determine if this was the manager's first active GW
        const isStartGW = teamData.startGW === currentGW;

        let newTransfers;

        if (isStartGW || isUnlimitedFT(prevFreeRaw)) {
          // GW immediately after an unlimited GW (the manager's first GW, or any
          // GW where FT was manually set to unlimited): always exactly 1 free
          // transfer. No rollover from an unlimited week.
          newTransfers = 1;
        } else {
          // Standard FPL-style rollover: remaining + 1, capped by league max.
          const remaining = Math.max(prevFree - prevUsed, 0);
          let nextFree = remaining + 1;
          if (nextFree > cap) nextFree = cap;
          if (nextFree < 1) nextFree = 1;
          newTransfers = nextFree;
        }

        if (!Number.isFinite(newTransfers)) {
          console.warn(`⚠️ NaN detected for team ${teamID} transfersFree → defaulting to 1`);
          newTransfers = 1;
        }

        // Build squadMeta for nextGW based on last.selections?.[currentGW]
        const selectionsForCurrentGW = last.selections?.[currentGW] || {};
        const squadMeta = {};
        for (const pos of leaguePositions) {
          const playersInPos = selectionsForCurrentGW[pos] || [];
          squadMeta[pos] = {};
          for (const playerID of playersInPos) {
            const playerDocRef = doc(paths.leaguePlayers(leagueID), playerID);
            const playerDocSnap = await getDoc(playerDocRef);
            let resolvedPrice = null;
            if (playerDocSnap.exists()) {
              const playerData = playerDocSnap.data();
              if (playerData.currentPrice !== undefined) {
                resolvedPrice = playerData.currentPrice;
              } else if (playerData.price !== undefined) {
                resolvedPrice = playerData.price;
              }
            }
            // Updated logic for resolving previous player meta
            // Robustly flatten squadMeta that can be stored as arrays OR objects keyed by playerId
            const flattenSquadMeta = (metaForGW) => {
              const acc = [];
              if (!metaForGW || typeof metaForGW !== "object") return acc;

              // metaForGW is like { DEF: [...]/map, MID: [...]/map, FWD: [...]/map }
              for (const bucket of Object.values(metaForGW)) {
                if (!bucket || typeof bucket !== "object") continue;

                if (Array.isArray(bucket)) {
                  // Array form: [{ id, buyGW, buyPrice }, ...]
                  bucket.forEach((entry) => {
                    if (entry && typeof entry === "object" && (entry.id || entry.buyGW !== undefined || entry.buyPrice !== undefined)) {
                      acc.push(entry.id ? entry : { ...entry });
                    }
                  });
                } else {
                  // Map form: { [playerId]: { buyGW, buyPrice }, ... }
                  for (const [playerId, val] of Object.entries(bucket)) {
                    if (val && typeof val === "object") {
                      if ("id" in val) {
                        acc.push(val);
                      } else if ("buyGW" in val || "buyPrice" in val) {
                        acc.push({ id: playerId, buyGW: val.buyGW, buyPrice: val.buyPrice });
                      }
                    }
                  }
                }
              }
              return acc;
            };

            let resolvedBuyGW;
            let resolvedBuyPrice;

            // Use currentGW for lookup (carry forward from the most recent snapshot)
            const lookupGW = currentGW;

            // 1) Try to find previous meta in last snapshot
            let prevPlayer = null;
            let allPrevPlayers = flattenSquadMeta(last.squadMeta?.[lookupGW]);

            prevPlayer = allPrevPlayers.find((p) => p.id === playerID) || null;

            // 2) Fallback: look in league-level team doc's squadMeta for the same GW
            if (!prevPlayer) {
              try {
                const leagueTeamRef = doc(paths.leagueTeams(leagueID), teamID);
                const leagueTeamSnap = await getDoc(leagueTeamRef);
                if (leagueTeamSnap.exists()) {
                  const leagueTeamData = leagueTeamSnap.data();
                  const leaguePrevPlayers = flattenSquadMeta(leagueTeamData?.squadMeta?.[lookupGW]);
                  prevPlayer = leaguePrevPlayers.find((p) => p.id === playerID) || null;
                }
              } catch (_) {
                // ignore and treat as new buy if not found
              }
            }

            if (prevPlayer) {
              // Player already existed in previous GW squadMeta: preserve their original buy info
              resolvedBuyGW = prevPlayer.buyGW;
              resolvedBuyPrice = prevPlayer.buyPrice;
            } else {
              // Player is newly added this GW: stamp fresh buy info using current (end-of-GW) price
              resolvedBuyGW = nextGW;
              resolvedBuyPrice = resolvedPrice;
            }
            squadMeta[pos][playerID] = {
              buyPrice: resolvedBuyPrice,
              buyGW: resolvedBuyGW,
            };
          }
        }

        // --- Compute starting bank for nextGW (read from league-level team doc currentBudget) ---
        let bankStartNext = null;
        try {
          // Try to read value from league-level team doc: currentBudget[currentGW]
          const leagueTeamRef = doc(paths.leagueTeams(leagueID), teamID);
          const leagueTeamSnap = await getDoc(leagueTeamRef);
          let cb = undefined;
          if (leagueTeamSnap.exists()) {
            const leagueTeamData = leagueTeamSnap.data();
            cb = leagueTeamData?.currentBudget?.[currentGW];
          }
          if (cb === undefined || cb === null || isNaN(Number(cb))) {
            bankStartNext = BASE_BUDGET;
          } else {
            bankStartNext = Number(cb);
          }
        } catch (e) {
          console.warn("⚠️ Failed to read currentBudget for bankStartNext; defaulting to BASE_BUDGET", e);
          bankStartNext = BASE_BUDGET;
        }

        // Determine if this team is starting fresh this GW
        let payload;
        let bankStartValue = Number((bankStartNext ?? 0).toFixed(2));
        if (teamData.startGW === nextGW) {
          // fresh start values
          payload = {
            leagueID,
            teamID,
            managerID: teamData.managerID,
            selections: { [nextGW]: {} },
            squadMeta: { [nextGW]: squadMeta },
            captains: { [nextGW]: "" },
            chipsUsed: { [nextGW]: "None" },
            bankStart: { [nextGW]: Number(bankStartValue) },
            currentBudget: { [nextGW]: Number(bankStartValue) },
            lastSavedAt: serverTimestamp(),
          };
        } else {
          // carry selections/captains, reset chip, include maxFreeTransfers, add id/number
          payload = {
            leagueID,
            teamID,
            managerID: teamData.managerID,
            selections: { [nextGW]: last.selections?.[currentGW] || {} },
            squadMeta: { [nextGW]: squadMeta },
            captains: { [nextGW]: last.captains?.[currentGW] || "" },
            chipsUsed: { [nextGW]: "None" },
            bankStart: { [nextGW]: Number(bankStartValue) },
            currentBudget: { [nextGW]: Number(bankStartValue) },
            lastSavedAt: serverTimestamp(),
          };
        }
        const selectedIdsForNextGW = selectedPlayerIdsFromSelection(payload.selections?.[nextGW] || {});
        for (const selectedPlayerID of selectedIdsForNextGW) {
          selectedByCounts.set(selectedPlayerID, (selectedByCounts.get(selectedPlayerID) || 0) + 1);
        }

        const teamGWRef = doc(paths.gwTeams(leagueID, nextGW), teamID);
        try {
          await setDoc(teamGWRef, payload);
          // --- Ensure league-level team doc snapshot for this GW ---
          try {
            // Only update transfersFree and transfersMade for nextGW if nextGW !== currentGW
            const updateObj = {
              [`selections.${currentGW}`]: last.selections?.[currentGW] || {},
              [`captains.${currentGW}`]: last.captains?.[currentGW] || "",
              [`chipsUsed.${currentGW}`]: last.chipsUsed?.[currentGW] || "None",
              [`currentBudget.${nextGW}`]: Number(bankStartValue),
              [`bankStart.${nextGW}`]: Number(bankStartValue),
            };
            // STRONG SAFEGUARD: Never write transfersFree/transfersMade for currentGW here
            if (nextGW !== currentGW) {
              updateObj[`transfersFree.${nextGW}`] = Number(newTransfers);
              updateObj[`transfersMade.${nextGW}`] = Number(0);
            } else {
              console.warn("🚫 Skipped unintended transfersFree/transfersMade write for currentGW", currentGW);
            }
            await updateDoc(doc(paths.leagueTeams(leagueID), teamID), updateObj);
          } catch (err) {
            console.error(`❌ Failed to update league-level team doc for ${teamID} during advance`, err);
            await logError({
              leagueID,
              userID: "system",
              action: "advanceGameweek_league_team_doc_advance",
              payload: { teamID, currentGW, nextGW },
              error: err
            });
          }
        } catch (err) {
          console.error(`❌ Failed to set GW snapshot for team ${teamID} at ${teamGWRef.path}`, err);
          await logError({
            leagueID,
            userID: "system",
            action: "advanceGameweek_team_snapshot",
            payload: { teamID, currentGW, nextGW },
            error: err
          });
          throw err;
        }
        try {
          // Only update transfersFree and transfersMade for nextGW if nextGW !== currentGW
          const updateObj = {
            maxFreeTransfers: Number(newTransfers),
            [`currentBudget.${nextGW}`]: Number(bankStartValue),
            [`bankStart.${nextGW}`]: Number(bankStartValue),
          };
          // STRONG SAFEGUARD: Never write transfersFree/transfersMade for currentGW here
          if (nextGW !== currentGW) {
            updateObj[`transfersFree.${nextGW}`] = Number(newTransfers);
            updateObj[`transfersMade.${nextGW}`] = Number(0);
          } else {
            console.warn("🚫 Skipped unintended transfersFree/transfersMade write for currentGW", currentGW);
          }
          await updateDoc(doc(paths.leagueTeams(leagueID), teamID), updateObj);
        } catch (err) {
          console.error(
            `❌ Failed to update team ${teamID} meta in leagueTeams at path: ${doc(paths.leagueTeams(leagueID), teamID).path}`,
            err
          );
          await logError({
            leagueID,
            userID: "system",
            action: "advanceGameweek_team_meta_update",
            payload: { teamID, currentGW, nextGW },
            error: err
          });
          throw err;
        }
      } catch (err) {
        console.error(`❌ Error processing team ${tDoc.id} during advanceGameweek:`, err);
        await logError({
          leagueID,
          userID: "system",
          action: "advanceGameweek_team_processing",
          payload: { teamID: tDoc.id, currentGW, nextGW },
          error: err
        });
        // Continue to next team
        continue;
      }
    }

    // 4.5 Update players with pointsHistory, priceHistory, cumulative stats, and selectionStats,
    // then apply price changes (if enabled)
    const playersCollection = paths.leaguePlayers(leagueID);
    const playersSnap = await getDocs(playersCollection);
    for (const playerDoc of playersSnap.docs) {
      const playerID = playerDoc.id;
      const playerData = playerDoc.data() || {};

      // Fetch playerPoints for currentGW from gameweeks/{gwID}/playerPoints/{playerID}
      const playerPointsRef = doc(
        paths.gwPlayerPoints(leagueID, currentGW),
        playerID
      );
      const playerPointsSnap = await getDoc(playerPointsRef);
      const playerPointsData = playerPointsSnap.exists() ? playerPointsSnap.data() : {};
      const pointsForCurrentGW = playerPointsData.total !== undefined ? playerPointsData.total : 0;

      // Prepare updated pointsHistory and priceHistory
      const pointsHistory = { ...(playerData.pointsHistory || {}) };
      pointsHistory[currentGW] = pointsForCurrentGW;

      // Snapshot player price at the end of the currentGW (before price changes are applied)
      const priceHistory = { ...(playerData.priceHistory || {}) };
      const currentPrice = playerData.currentPrice !== undefined
        ? playerData.currentPrice
        : (playerData.price !== undefined ? playerData.price : null);

      if (currentPrice !== null) {
        priceHistory[currentGW] = Number(currentPrice);
      }

      const existingStats = emptyPlayerStats(playerData.stats || {});
      const existingStatsMeta = playerData.statsMeta || {};
      const alreadyUpdatedThisGW = existingStatsMeta.lastStatsGW === currentGW;
      const statDelta = extractPlayerPointStats(playerPointsData, playerData, rules);
      const nextStats = alreadyUpdatedThisGW
        ? existingStats
        : addStats({ ...existingStats }, statDelta);

      const selectionCount = selectedByCounts.get(playerID) || 0;
      const selectionPct = totalTeamsForSelection
        ? Number(((selectionCount / totalTeamsForSelection) * 100).toFixed(1))
        : 0;

      const playerUpdatePayload = {
        pointsHistory,
        priceHistory,
        currentPrice,
        stats: nextStats,
        selectionStats: {
          selectedByTeams: selectionCount,
          selectedPct: selectionPct,
        },
        statsMeta: {
          ...existingStatsMeta,
          lastStatsGW: currentGW,
          updatedAt: serverTimestamp(),
        },
      };

      try {
        await updateDoc(
          doc(paths.leaguePlayers(leagueID), playerID),
          playerUpdatePayload
        );
      } catch (err) {
        console.error(`❌ Failed to update player ${playerID} with pointsHistory, priceHistory, stats, and selectionStats:`, err);
        await logError({
          leagueID,
          userID: "system",
          action: "advanceGameweek_player_update",
          payload: { playerID, currentGW, nextGW },
          error: err
        });
        throw err;
      }
    }

    // Only apply price changes if price movement is enabled in league config
    if (league.priceMovementEnabled !== false) {
      await applyPriceChanges(leagueID, currentGW, nextGW);
    }

    // 5. Update league meta and phase progression
    const leagueUpdateData = {
      currentGW: nextGW,
      lastAdvancedAt: serverTimestamp(),
      phase: "pick",
    };
    try {
      await updateDoc(leagueRef, leagueUpdateData);
    } catch (err) {
      console.error(`❌ Failed to update league meta at ${leagueRef.path}`, err);
      await logError({
        leagueID,
        userID: "system",
        action: "advanceGameweek_league_update",
        payload: { currentGW: currentGW, nextGW: nextGW },
        error: err
      });
      throw err;
    }
    // Also ensure the nextGW doc is up to date with phase and isCurrent (already done above, but make sure)
    try {
      await updateDoc(nextGWRef, { phase: "pick", isCurrent: true });
    } catch (_) {}
    // Ensure the previous GW doc is set to phase: "closed" (semantic clarity)
    try {
      await updateDoc(curGWRef, { phase: "closed" });
    } catch (_) {}

    // 6. Verification step: Check all leagueTeams have a gwTeams snapshot for the new GW
    try {
      const leagueTeamsSnap = await getDocs(paths.leagueTeams(leagueID));
      const gwTeamsSnap = await getDocs(paths.gwTeams(leagueID, nextGW));
      const leagueTeamIDs = leagueTeamsSnap.docs.map(doc => doc.id);
      const gwTeamIDs = gwTeamsSnap.docs.map(doc => doc.id);
      const missingTeamIDs = leagueTeamIDs.filter(id => !gwTeamIDs.includes(id));
      if (missingTeamIDs.length > 0) {
        console.warn(`⚠️ The following teams in leagueTeams/${leagueID} are missing gwTeams snapshots for ${nextGW}:`, missingTeamIDs);
      }
    } catch (err) {
      console.warn(`⚠️ Could not complete verification of gwTeams snapshots for ${nextGW}:`, err);
    }

    const verifyLeague = await getDoc(leagueRef);
    console.log(`✅ Advanced to ${nextGW} (league.currentGW now: ${verifyLeague.data()?.currentGW})`);
    // Usage tracking: gameweek advanced (non-blocking)
    try {
      await logEvent({
        eventType: "gameweek_advanced",
        leagueID,
        userID: actorUserID,
        userName: actorUserName,
        role: actorRole,
        source: actorSource,
        metadata: {
          fromGW: currentGW,
          toGW: nextGW,
          prevDeadline: (() => {
            const d = league?.deadline || null;
            return d && typeof d.toDate === "function" ? d.toDate().toISOString() : d ? new Date(d).toISOString() : "";
          })(),
          nextDeadline: deadline instanceof Date ? deadline.toISOString() : (deadline ? new Date(deadline).toISOString() : ""),
        },
      });
    } catch (e) {
      console.warn("[analytics] gameweek_advanced failed", e);
    }
    // Log success event -- do not let logging failure affect main function
    try {
      await logError({
        leagueID,
        userID: "system",
        action: "advanceGameweek_success",
        payload: { currentGW, nextGW, status: "completed" },
        error: null
      });
    } catch (logErr) {
      console.warn("⚠️ advanceGameweek_success log entry failed:", logErr);
    }
    return nextGW;
  } catch (err) {
    console.error("❌ Advance gameweek failed:", err);
    // Usage tracking: advance gameweek error (non-blocking)
    try {
      await logEvent({
        eventType: "page_error_caught",
        leagueID,
        userID: actorUserID,
        userName: actorUserName,
        role: actorRole,
        source: actorSource,
        metadata: {
          action: "advanceGameweek",
          message: err?.message || String(err),
        },
      });
    } catch (e) {
      console.warn("[analytics] page_error_caught (advance_gameweek) failed", e);
    }
    // Use currentGW if available, else fallback to "unknown"
    const currentGW = "unknown"; // fallback for logging outside main try block
    await logError({
      leagueID,
      userID: "system",
      action: "advanceGameweek_main",
      payload: {
        currentGW: typeof currentGW !== "undefined" ? currentGW : "unknown",
        nextGW: typeof currentGW !== "undefined"
          ? `GW${parseInt(currentGW.replace("GW", ""), 10) + 1}`
          : "unknown"
      },
      error: err
    });
    throw err;
  }
}